import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Send, ShieldCheck, Clock, Languages, Award } from "lucide-react";
import { useI18n } from "../lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TRUST_ICONS = [Clock, ShieldCheck, Languages, Award];

export const BookingForm = () => {
  const { t } = useI18n();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    location: "",
    vehicle: "",
    service_type: "",
    description: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service_type) {
      toast.error(t.booking.form.error);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/service-requests`, form);
      toast.success(t.booking.form.success);
      setForm({
        name: "",
        phone: "",
        location: "",
        vehicle: "",
        service_type: "",
        description: "",
      });
    } catch (err) {
      console.error(err);
      toast.error(t.booking.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative py-24 md:py-32 bg-[#0A0A0C] border-t border-zinc-900"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* LEFT - trust */}
          <div className="lg:col-span-5">
            <div className="font-condensed uppercase text-xs tracking-[0.4em] text-[#E51E1A] mb-4">
              · {t.booking.kicker}
            </div>
            <h2 className="font-headings uppercase text-5xl md:text-6xl lg:text-7xl text-white leading-none">
              {t.booking.title}
              <br />
              <span className="text-[#E51E1A]">{t.booking.titleAccent}</span>
            </h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
              {t.booking.subtitle}
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {t.booking.trust.map((it, i) => {
                const Icon = TRUST_ICONS[i] || CheckCircle2;
                return (
                  <div
                    key={i}
                    className="border border-zinc-800 p-5 hover:border-[#E51E1A]/60 transition"
                  >
                    <Icon className="w-6 h-6 text-[#E51E1A] mb-3" strokeWidth={1.8} />
                    <div className="font-headings uppercase text-white text-lg leading-tight">
                      {it.t}
                    </div>
                    <p className="mt-1 text-sm text-zinc-400 leading-snug">{it.d}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT - form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="booking-form"
              className="border border-zinc-800 bg-[#121215] p-6 md:p-10 grain"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field
                  label={t.booking.form.name}
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  testId="input-name"
                  required
                />
                <Field
                  label={t.booking.form.phone}
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  type="tel"
                  testId="input-phone"
                  required
                />
                <Field
                  label={t.booking.form.location}
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  testId="input-location"
                  full
                />
                <Field
                  label={t.booking.form.vehicle}
                  name="vehicle"
                  value={form.vehicle}
                  onChange={handleChange}
                  testId="input-vehicle"
                  full
                />

                <div className="md:col-span-2">
                  <label className="block font-condensed uppercase text-xs tracking-[0.3em] text-zinc-400 mb-2">
                    {t.booking.form.serviceType} *
                  </label>
                  <select
                    name="service_type"
                    value={form.service_type}
                    onChange={handleChange}
                    data-testid="input-service-type"
                    required
                    className="w-full bg-[#0A0A0C] border border-zinc-800 text-white px-4 py-3 font-body focus:border-[#E51E1A] focus:outline-none transition"
                  >
                    <option value="">{t.booking.form.selectService}</option>
                    {t.services.items.map((s, i) => (
                      <option key={i} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block font-condensed uppercase text-xs tracking-[0.3em] text-zinc-400 mb-2">
                    {t.booking.form.description}
                  </label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    data-testid="input-description"
                    rows={4}
                    className="w-full bg-[#0A0A0C] border border-zinc-800 text-white px-4 py-3 font-body focus:border-[#E51E1A] focus:outline-none transition resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit"
                className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#E51E1A] hover:bg-[#FF3B30] disabled:opacity-60 disabled:cursor-not-allowed text-white font-headings uppercase tracking-wider text-xl py-5 transition-all hover:-translate-y-0.5 shadow-[4px_4px_0px_0px_rgba(255,255,255,0.06)]"
              >
                <Send className="w-5 h-5" />
                {submitting ? t.booking.form.sending : t.booking.form.submit}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, type = "text", testId, required, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block font-condensed uppercase text-xs tracking-[0.3em] text-zinc-400 mb-2">
      {label} {required && "*"}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testId}
      required={required}
      className="w-full bg-[#0A0A0C] border border-zinc-800 text-white px-4 py-3 font-body focus:border-[#E51E1A] focus:outline-none transition"
    />
  </div>
);
